// Two-hand piano display+input for Chord Craft. Presentational, controlled
// by the parent — no internal note state beyond tracking which midis this
// component itself is holding down (to pair pointerdown/up cleanly). Layout
// technique is copied from src/components/pianoDrills/PianoRoll.js (absolute
// positioning of white/black keys, pointer capture so a dragged finger still
// releases the right key) — read that file before changing this one. Do NOT
// import or modify PianoRoll; this is a standalone sibling with role-based
// coloring instead of active/target coloring.
//
// Props: {
//   low, high: midi ints, inclusive — the full two-hand range rendered,
//   roles = {}: { [midi]: 'left' | 'right' | 'color' | 'wrong' } — target
//     display role for keys that matter right now (skeleton tones vs.
//     color/extension tones); keys with no entry render plain. 'wrong' is a
//     rose/red treatment for a currently-held note that doesn't belong to
//     the target (live wrong-note feedback) — it wins over any other role a
//     caller merges onto the same key,
//   activeMidis = new Set(): user-held notes (on-screen, MIDI, or qwerty),
//   onPress?(midi), onRelease?(midi): omit BOTH to render display-only (no
//     pointer handlers, no button semantics),
//   useFlats?: bool — note spelling for labels,
//   showLabels? = true: show the note name on every C,
//   hints = {}: { [midi]: 'scale' | 'chord' } — jam guidance dots rendered
//     near the bottom of a key (above the C label when both are present).
//     Purely decorative: doesn't touch the key's fill, coexists with roles/
//     activeMidis, and renders nothing when a key has no entry.
// }
//
// Hand colors are fixed by convention across Chord Craft: left hand = amber,
// right hand = sky, color/extension tones = fuchsia with a soft glow. A held
// note that also carries a role gets an emerald ring on top of its role
// color so "you're playing the right thing" reads at a glance — except
// 'wrong', which is already the "you're playing the wrong thing" signal and
// gets a rose ring instead.

import React, { useRef } from "react";
import { cn } from "../../lib/utils";
import { isBlackKey, midiToName } from "../../lib/pianoDrills";

const MIN_WHITE_KEY_PX = 28;
const WIDE_RANGE_SEMITONES = 36; // > 3 octaves

const buildKeys = (low, high) => {
  const keys = [];
  let wi = 0;
  for (let midi = low; midi <= high; midi += 1) {
    const black = isBlackKey(midi);
    keys.push({ midi, black, wi: black ? wi - 1 : wi });
    if (!black) wi += 1;
  }
  return keys;
};

// Literal Tailwind class strings per role (kept literal, not composed via
// template interpolation, so the JIT scanner always picks them up).
const ROLE_CLASSES = {
  left: {
    white: "bg-amber-400/90 border-amber-500 text-amber-950",
    black: "bg-amber-500 border-amber-700 text-amber-50",
  },
  right: {
    white: "bg-sky-400/90 border-sky-500 text-sky-950",
    black: "bg-sky-500 border-sky-700 text-sky-50",
  },
  color: {
    white:
      "bg-fuchsia-400/90 border-fuchsia-500 text-fuchsia-950 shadow-[0_0_12px_rgba(232,121,249,0.75)]",
    black:
      "bg-fuchsia-500 border-fuchsia-700 text-fuchsia-50 shadow-[0_0_12px_rgba(232,121,249,0.85)]",
  },
  wrong: {
    white: "bg-rose-500/80 border-rose-600 text-rose-50",
    black: "bg-rose-400 border-rose-600 text-rose-50",
  },
};

// Literal Tailwind class strings per hint type (kept literal for the same
// JIT-scanning reason as ROLE_CLASSES above).
const HINT_DOT_CLASSES = {
  scale: {
    white: "bg-stone-400/50",
    black: "bg-stone-500/60",
  },
  chord: {
    white: "bg-sky-400",
    black: "bg-sky-300",
  },
};

export default function CraftPiano({
  low,
  high,
  roles = {},
  activeMidis = new Set(),
  onPress,
  onRelease,
  useFlats = false,
  showLabels = true,
  hints = {},
}) {
  const downRef = useRef(new Set());
  const interactive = typeof onPress === "function";
  const active = activeMidis || new Set();

  const keys = buildKeys(low, high);
  const whiteCount = keys.filter((k) => !k.black).length || 1;
  const W = 100 / whiteCount; // % width per white key
  const BW = W * 0.6; // black-key width
  const isWide = high - low > WIDE_RANGE_SEMITONES;
  const minWidthPx = whiteCount * MIN_WHITE_KEY_PX;

  const start = (midi) => {
    if (downRef.current.has(midi)) return;
    downRef.current.add(midi);
    onPress(midi);
  };
  const end = (midi) => {
    if (!downRef.current.has(midi)) return;
    downRef.current.delete(midi);
    if (onRelease) onRelease(midi);
  };

  const handlePointerDown = (midi) => (e) => {
    e.preventDefault();
    if (e.currentTarget.setPointerCapture && e.pointerId != null) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (err) {
        // stale pointer id — harmless
      }
    }
    start(midi);
  };
  const handleEnd = (midi) => () => end(midi);

  return (
    <div className={cn(isWide && "overflow-x-auto")}>
      <div
        className="craft-piano relative h-36"
        style={isWide ? { minWidth: `${minWidthPx}px` } : undefined}
        role="group"
        aria-label={`piano keyboard, ${midiToName(low, useFlats)} to ${midiToName(high, useFlats)}`}
      >
        {keys.map((k) => {
          const left = k.black ? (k.wi + 1) * W - BW / 2 : k.wi * W;
          const width = k.black ? BW : W;
          const role = roles[k.midi];
          const isActive = active.has(k.midi);
          const roleClass = role ? ROLE_CLASSES[role]?.[k.black ? "black" : "white"] : null;
          const showName = showLabels && k.midi % 12 === 0; // every C
          const hint = hints[k.midi];

          const Tag = interactive ? "button" : "div";

          return (
            <Tag
              key={k.midi}
              {...(interactive ? { type: "button" } : {})}
              data-midi={k.midi}
              data-role={role || undefined}
              className={cn(
                "craft-piano__key touch-none select-none absolute top-0 flex flex-col items-center justify-end gap-0.5 pb-1 rounded-b-md border transition-transform duration-75",
                interactive ? "cursor-pointer" : "cursor-default",
                k.black
                  ? "craft-piano__key--black z-10 h-24 bg-stone-900 border-stone-950 text-stone-400"
                  : "craft-piano__key--white z-0 h-36 bg-stone-100 border-stone-400 text-stone-500",
                roleClass,
                isActive && "craft-piano__key--active translate-y-0.5 brightness-125",
                isActive && role && role !== "wrong" && "ring-2 ring-inset ring-emerald-400",
                isActive && role === "wrong" && "ring-2 ring-inset ring-rose-300"
              )}
              style={{ left: `${left}%`, width: `${width}%` }}
              aria-label={midiToName(k.midi, useFlats)}
              aria-pressed={interactive ? isActive : undefined}
              {...(interactive
                ? {
                    onPointerDown: handlePointerDown(k.midi),
                    onPointerUp: handleEnd(k.midi),
                    onPointerCancel: handleEnd(k.midi),
                    onPointerLeave: handleEnd(k.midi),
                  }
                : {})}
            >
              {hint && (
                <span
                  aria-hidden="true"
                  className={cn(
                    "craft-piano__hint pointer-events-none rounded-full",
                    hint === "chord" ? "w-[7px] h-[7px]" : "w-[6px] h-[6px]",
                    HINT_DOT_CLASSES[hint]?.[k.black ? "black" : "white"]
                  )}
                />
              )}
              {showName && (
                <span className="text-[10px] font-medium leading-none pointer-events-none">
                  {midiToName(k.midi, useFlats)}
                </span>
              )}
            </Tag>
          );
        })}
      </div>
    </div>
  );
}
